import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'ra-core';
import { revoquerUnAcces } from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur } from '../../fournisseurs/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **ANNULER L'ACCÈS D'UNE PERSONNE À UN COURS — `CDC-1` F-12, ET C'ÉTAIT LA
 * DERNIÈRE LIGNE DE DETTE DE CE DÉPÔT QUI SE FERME PAR UN ÉCRAN.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Le geste était inatteignable par construction depuis le LOT-1**, et le motif n'était
 * pas un oubli : la route s'adressait par un `AutologinLink.id`, **qu'aucune réponse du
 * contrat ne porte ni ne peut porter** — `CDC-5` F-59 : « **aucune surface ne montre les
 * codes de vérification ni les liens d'auto-connexion en cours** » (Q144, Q148). D-129 l'a
 * mesuré, D-130 arbitrage 3 l'a tranché : **(B), la route se ré-adresse par `(cours,
 * personne)`**, `DELETE /api/v1/acces/cours/{coursId}/personnes/{personneId}`.
 *
 * **F-59 tient sans un mot d'amendement, et c'est tout l'objet de l'arbitrage** : ce que
 * cet écran nomme est **une personne et un cours**, jamais un lien. Aucun secret n'entre
 * ici, ni en réponse, ni à l'écran, ni dans une adresse.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **UN GESTE SUR LA LIGNE, COMME F-63 LES RANGE — ET C'EST LE PATRON DE D-120.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-62 : « **deux des dix écrans rendus ne sont pas des écrans** ». F-63 range les gestes
 * d'un cours sur sa ligne — modifier, couper, masquer, observer —, et D-120 a posé la
 * modification de la même façon : « **un geste sur la ligne**, et non un écran d'édition de
 * ressource, ce qui aurait fait de "cours" une ressource modifiable là où le corpus n'en
 * fait qu'une liste ». Celui-ci se pose au même endroit, pour la même raison.
 *
 * **La liste des participants n'est pas relue** : elle est **déjà sur la ligne**, F-62 la
 * mettant dans ce que la ligne porte — « participants **par leur prénom**, la structure de
 * chaque participant ». Une seconde lecture aurait été un second inventaire de la même
 * donnée, ce que D-130 arbitrage 2 vient de retirer du contrat trois fois.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE RÉ-ADRESSAGE NE SUFFISAIT PAS, ET IL A FALLU UN CHAMP DE PLUS AU CONTRAT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * D-130 écrit que le geste devient « adressable depuis ce que les écrans montrent déjà :
 * **un cours, ses participants** ». **Mesuré le 20260829 : le cours l'était, la personne
 * ne l'était pas.** `listerLesCoursDuneStructure` rendait de chaque participant `prenom`,
 * `anonymise`, `structureId` et `role` — **et aucun identifiant**. Le geste restait donc
 * inatteignable **un pas plus loin, mais inatteignable pareil**.
 *
 * **C'est la leçon de D-108 prise un cran plus loin** — « une route qui existe n'est pas
 * une route atteignable » : une route **adressable en principe** n'est pas une route
 * **adressable depuis l'écran**. L'arbitrage a établi que la route acceptait le couple ;
 * il n'a pas établi qu'un écran pouvait le former.
 *
 * **`personneId` entre donc au contrat, et il ne coûte aucune lecture** : `userId` était
 * déjà sélectionné par `CHAMPS_DE_LIGNE` et **jeté au mapping**. Motif complet au
 * contrat, `api/src/admin/dto/cours.responses.ts`.
 *
 * **Rien n'est contourné pour autant** : ni second appel pour retrouver l'identifiant par
 * le prénom — « un geste, un chemin » (Q566), et deux chemins pour un geste font deux
 * autorisations à tenir d'accord.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES DEUX RÔLES L'ONT, ET §6.14 N'A PAS DE CELLULE POUR CE GESTE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `CDC-1` §7 donne les quatre identités de la route : « **`tutor` (son cours), `orgadmin`,
 * secret de structure, `superadmin`** ». Les deux rôles de cette surface y sont **tous les
 * deux**, sans condition de rôle — la seule borne est de **périmètre**, et elle vit sur la
 * route.
 *
 * **`CDC-5` §6.14 ne porte aucune ligne pour ce geste**, la révocation d'un accès étant une
 * règle de `CDC-1` que la matrice n'a pas reprise. Le geste ne consulte donc **aucune
 * cellule** : en inventer une ici serait « une condition enfouie dans un écran », et
 * consulter une action absente de la table la rendrait **`false` pour les deux rôles**,
 * c'est-à-dire un geste à nouveau inatteignable. **La cellule due est remontée** ; d'ici
 * là, le sens du défaut est celui que `matrice.ts` déclare lui-même — « cette table peut
 * être **trop permissive sans danger**, le serveur refuse ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA BORNE DE PÉRIMÈTRE N'EST PAS RECOPIÉE ICI, ET C'EST LE PATRON DE `GestesSurUnCompte`.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Un `orgadmin` agit sur **les cours portant sa structure** — celle du **tuteur** —, quand
 * la liste, elle, se borne sur **les participants**. Ce sont **deux colonnes différentes**,
 * `access.service.ts` l'écrit à côté de sa requête : un cours peut donc être lisible ici et
 * hors périmètre pour ce geste-là.
 *
 * **La borne reste au serveur.** C'est ce que la ressource « comptes » a déjà tranché pour
 * les relations : « il montre le bouton sur **toutes**, et **laisse le serveur trancher
 * l'objet**. Filtrer ici recopierait la borne — troisième copie —, et c'est celle qui
 * divergerait. » Le refus arrive nommé, `INTROUVABLE`, et il est traduit plus bas avec la
 * règle qui l'explique.
 *
 * **Ce n'est pas la leçon de D-111 prise à l'envers**, et la distinction est celle que D-111
 * fait lui-même : ce que ce défaut interdit est un bouton refusé **par le rôle**, toujours et
 * pour tout le monde — « un bouton qui mène à un refus est cette confusion, servie à la
 * personne qui administre ». Un refus **d'objet**, lui, dépend de la ligne, et le corpus le
 * veut au serveur : « une borne d'autorisation qui dépend du chemin emprunté est une faille
 * par construction ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE L'ÉTAT DU COURS DÉCIDE, ET IL A ÉTÉ MESURÉ AVANT D'ÊTRE ÉCRIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Le geste ne se propose que sur un cours en cours.** À la fin d'un cours,
 * `courses.service.ts` passe **tous** les liens encore émis à `EXPIRED_COURSE_ENDED` — « un
 * lien qui n'a pas servi ne vaut plus ». Sur un cours terminé, la route ne peut donc rendre
 * que `dejaSansEffet`, **quoi qu'on lui demande** : un bouton qui ne peut jamais rien faire
 * est le second défaut de D-111, « un bouton qui a l'air d'agir ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **`PERSONNE_PRESENTE_DANS_UN_COURS` : LE REFUS EST BRANCHÉ, ET IL NE VISE PAS
 * CETTE SURFACE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `CDC-1` F-12 critère 3 : « **SI un appel présentant un secret** tente d'annuler l'accès
 * d'une personne **présente** dans un cours, ALORS LE SYSTÈME DOIT le rejeter avec l'erreur
 * `PERSONNE_PRESENTE_DANS_UN_COURS`. » Et `CDC-2` F-15 dit les deux moitiés de la règle
 * dans la même ligne : « **une révocation d'accès éjecte immédiatement**, même en pleine
 * séance (Q94), et ferme immédiatement le canal de visio de la personne (Q433). **Un secret
 * de structure ne peut pas révoquer l'accès d'une personne présente** (Q259). »
 *
 * **La borne ne vaut donc que pour l'appel présentant un secret de structure**, et
 * `access.service.ts` la pose exactement là — `caller.kind === 'organization'`. **Cette
 * surface est toujours une personne** : le refus n'y est pas atteignable. Il est branché
 * quand même, et sa phrase dit **le geste qui le lève** plutôt que de répéter le refus.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CET ÉCRAN N'AFFIRME PAS, ET C'EST DÉLIBÉRÉ — D-111.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **IL PROMET L'ÉJECTION DEPUIS LE 20260830, ET IL NE LA PROMETTAIT PAS AVANT.** Cet
 * en-tête portait : « **il ne promet pas l'éjection immédiate** […] ce que la route écrit
 * aujourd'hui est le passage des liens encore émis à `révoqué`, et rien d'autre : elle ne
 * touche ni `Participant.status`, ni le canal. L'affirmer serait *une garantie dont
 * l'exécution repose sur une valeur que rien ne contraint*. » **C'était juste, et c'est ce
 * refus d'affirmer qui a produit la carte #33.**
 *
 * `CDC-2` F-15 critère 2 est désormais servi : la route écrit `EJECTED` sur un porteur
 * présent, **ferme sa ligne de relevé** avec la cause « accès annulé », et son état ferme
 * les trois chemins — l'entrée, le socket, le droit d'entrée au canal de visio.
 *
 * **CE QU'IL DIT RESTE CE QUE LA RÉPONSE REND, ET RIEN DE PLUS** : `ejecte` — la personne
 * était présente et vient d'être sortie du cours —, `revoque` — au moins un accès valait
 * encore et ne vaut plus —, `dejaSansEffet` — aucun ne valait plus. **Les trois ne se
 * déduisent pas l'un de l'autre** : une personne éjectée peut avoir `revoque` faux, son
 * accès ayant été consommé à l'entrée.
 *
 * **ET IL N'AFFIRME TOUJOURS PAS QUE L'IMAGE S'EST ARRÊTÉE.** Le droit d'entrée au canal
 * ne se renouvelle plus et le socket est refusé, mais **la liaison de visio déjà montée
 * chez le fournisseur n'est coupée par aucune ligne du produit** : elle meurt au terme du
 * droit, dix minutes au plus. Le critère dit « **sans attendre le terme de son droit
 * d'entrée** » — cette moitié-là est **remontée en D-146**, pas comblée ici, et cet écran
 * ne la promet pas.
 */

/**
 * Un participant, tel que la ligne du cours le porte.
 *
 * **`personneId` EST ENTRÉ AU CONTRAT LE 20260829, ET C'EST LUI QUI REND CE GESTE
 * EXERÇABLE.** La ligne portait le prénom, la structure et le rôle — **et rien pour
 * désigner** —, donc le geste ré-adressé par D-130 arbitrage 3 restait inatteignable
 * un pas plus loin. C'est la forme de D-108 : l'arbitrage a établi que la route
 * acceptait le couple, pas qu'un écran pouvait le former.
 *
 * **Il ne s'affiche pas, il adresse** : ce n'est donc pas un élargissement de la liste
 * fermée de F-62, qui énumère ce que la ligne **montre**. Et ce n'est pas un secret —
 * F-59 interdit de montrer un lien d'auto-connexion, jamais de nommer une personne,
 * que cette ligne nomme déjà par son prénom.
 */
type ParticipantDeLaLigne = {
  readonly prenom: string | null;
  readonly anonymise: boolean;
  readonly structureId: string;
  readonly role: 'TUTOR' | 'STUDENT';
  readonly personneId: string;
};

type CoursDeLaLigne = {
  readonly id: string;
  readonly titre: string;
  readonly enCours: boolean;
  readonly participants: readonly ParticipantDeLaLigne[];
};

type Resultat = {
  readonly nom: string;
  readonly revoque: boolean;
  readonly dejaSansEffet: boolean;
  /** Vrai quand la personne était PRÉSENTE et vient d'être éjectée — F-15 critère 2. */
  readonly ejecte: boolean;
};

/** **La désignation neutre de F-62**, la même que la colonne des participants. */
function designer(participant: ParticipantDeLaLigne): string {
  if (participant.anonymise) return '(compte anonymisé)';
  return participant.prenom ?? '—';
}

export function RevocationDunAcces() {
  const cours = useRecordContext<CoursDeLaLigne>();
  const notifier = useNotify();
  const rafraichir = useRefresh();

  const [ouvert, setOuvert] = useState(false);
  const [aConfirmer, setAConfirmer] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState<Resultat | null>(null);

  if (cours === undefined) return null;

  // **Sur un cours terminé, tous les liens sont déjà caducs** : le geste ne peut rien
  // faire, donc il ne se propose pas. Voir le docblock.
  if (!cours.enCours) return null;

  const participants = cours.participants;

  const ouvrir = () => {
    setAConfirmer(null);
    setResultat(null);
    setOuvert(true);
  };

  const revoquer = async (participant: ParticipantDeLaLigne) => {
    const personneId = participant.personneId;
    setEnCours(true);
    try {
      const traite = await deballer(
        revoquerUnAcces({ path: { coursId: cours.id, personneId } }),
      );
      setResultat({
        nom: designer(participant),
        revoque: traite.revoque,
        dejaSansEffet: traite.dejaSansEffet,
        ejecte: traite.ejecte,
      });
      setAConfirmer(null);
      rafraichir();
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      notifier(messageDeRefus(lue.code, lue.message), { type: 'error' });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={ouvrir}>
        Annuler un accès
      </Button>

      <Dialog open={ouvert} onOpenChange={setOuvert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuler l’accès d’une personne à ce cours</DialogTitle>
            <DialogDescription>
              Le geste désigne une <strong>personne</strong> et un{' '}
              <strong>cours</strong>, jamais un lien : aucune surface ne montre
              les liens d’auto-connexion en cours. Il annule{' '}
              <strong>tout ce qui vaut encore</strong> pour ce couple — un accès
              réémis laisserait sinon un second lien valide.
            </DialogDescription>
          </DialogHeader>

          <p className="text-muted-foreground text-xs">
            Cours : « {cours.titre} ».
          </p>

          {participants.length === 0 ? (
            <p className="text-sm">Ce cours ne porte aucun participant.</p>
          ) : (
            <ul className="flex flex-col gap-2 text-sm">
              {participants.map((participant) => (
                <li
                  key={participant.personneId}
                  className="flex items-center gap-3"
                >
                  <span className="font-medium">{designer(participant)}</span>
                  <Badge variant="outline">
                    {participant.role === 'TUTOR' ? 'tuteur' : 'élève'}
                  </Badge>
                  <span className="text-muted-foreground text-xs">
                    {participant.structureId}
                  </span>
                  {aConfirmer === participant.personneId ? (
                    <span className="ml-auto flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAConfirmer(null)}
                      >
                        Non
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={enCours}
                        onClick={() => void revoquer(participant)}
                      >
                        Confirmer l’annulation
                      </Button>
                    </span>
                  ) : (
                    <Button
                      className="ml-auto"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setResultat(null);
                        setAConfirmer(participant.personneId);
                      }}
                    >
                      Annuler l’accès
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {resultat !== null && (
            <Alert>
              <AlertTitle>
                {resultat.revoque
                  ? `Accès annulé — ${resultat.nom}.`
                  : `Aucun accès ne valait plus — ${resultat.nom}.`}
              </AlertTitle>
              <AlertDescription>
                {/* **L'ÉJECTION SE DIT AVANT TOUT LE RESTE, ET C'EST L'ORDRE QUI
                    COMPTE.** Elle a coupé quelqu'un en pleine séance : c'est le fait
                    le plus lourd de la réponse, et la personne qui vient de révoquer
                    doit le lire en premier. Les deux autres champs disent l'état des
                    liens, ce qui est une conséquence administrative. */}
                <span className="block">
                  {resultat.ejecte
                    ? 'Cette personne était PRÉSENTE dans le cours : elle vient d’en être ' +
                      'éjectée. Sa ligne de relevé est fermée avec la cause « accès annulé », ' +
                      'son poste ne peut plus rien écrire, et une nouvelle entrée lui est ' +
                      'refusée.'
                    : 'Cette personne n’était pas présente dans le cours. Son accès est ' +
                      'annulé avant son arrivée, et une entrée lui est refusée.'}
                </span>
                <span className="mt-2 block">
                  {resultat.dejaSansEffet
                    ? 'Aucun accès ne valait déjà plus — consommé, révoqué, remplacé, échu, ' +
                      'ou son cours terminé. Ce n’est pas une erreur : le geste visait à ce ' +
                      'qu’il ne vaille plus, et il ne vaut plus.'
                    : 'Les accès encore émis pour cette personne sur ce cours sont annulés.'}
                </span>
                {resultat.ejecte && (
                  /**
                   * **CE QUE L'ÉCRAN NE PROMET PAS, ET LE DIRE EST LE POINT.** Le
                   * droit d'entrée au canal de visio ne se renouvelle plus, mais la
                   * liaison déjà montée chez le fournisseur n'est coupée par aucune
                   * ligne du produit. F-15 critère 2 dit « sans attendre le terme de
                   * son droit d'entrée » : cette moitié est remontée, pas servie.
                   */
                  <span className="mt-2 block">
                    Son image et son son peuvent continuer jusqu’au terme de son
                    droit d’entrée au canal, <strong>dix minutes au plus</strong> :
                    le produit ne coupe pas une liaison de visio déjà montée. Pour
                    l’arrêter tout de suite, coupez ses flux depuis l’écran du
                    cours.
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOuvert(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * **Les refus, branchés sur `code` et jamais sur `message`** — `DOCTRINE.md` §5 : « `code`
 * est le contrat réel : c'est sur lui que le front branche son comportement, jamais sur
 * `message`, qui est destiné à l'humain et peut changer sans préavis ».
 */
function messageDeRefus(code: string, message: string): string {
  if (code === 'PERSONNE_PRESENTE_DANS_UN_COURS') {
    return (
      'Cette personne est présente dans le cours. Ce refus ne vise que les appels ' +
      'présentant le secret d’une structure — « un cours commencé va à son terme ». ' +
      'Exercée par une personne, le tuteur du cours, celle qui tient l’espace de la ' +
      'structure ou le compte qui voit tout, la même annulation vaut : c’est elle qui ' +
      'éjecte le porteur, même en pleine séance. Depuis cette surface vous agissez comme ' +
      'une personne, donc ce refus ne devrait pas vous atteindre.'
    );
  }
  if (code === 'INTROUVABLE') {
    return (
      'Cet accès est introuvable, ou hors de votre périmètre — les deux cas rendent la ' +
      'même réponse. La personne qui tient l’espace d’une structure n’annule un accès que ' +
      'sur les cours portant SA structure, c’est-à-dire celle du tuteur ; la liste, elle, ' +
      'montre les cours où l’un de ses élèves se trouve. Ce sont deux bornes différentes.'
    );
  }
  return message;
}
